import { PlusOutlined, RightOutlined } from '@ant-design/icons';
import { Button, ConfigProvider, Dropdown, Flex, Grid, Space, Tooltip } from 'antd';
import * as Icon from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';

import useStore from '../../store/useStore';
import CreateBrand from '../../views/profile/CreateBrand';
import CreateUser from '../../views/profile/CreateUser';

/**
 * Хажуугийн менюний "Шинэ" товч.
 *
 * Харагдацыг бүхэлд нь antd-ийн API-аар (ConfigProvider-ийн token, `Flex`,
 * товчны prop) тодорхойлно — LESS класс, `.ant-*` дарж бичих хэрэггүй.
 */

/** Брэндийн хөх — App.js дахь `colorPrimary`-тэй ижил */
const BRAND_COLOR = '#2fa1d9';
const BRAND_COLOR_HOVER = '#1c8dc4';

const brandAlpha = (alpha) => `rgba(47, 161, 217, ${alpha})`;

/** Товчны өндөр — 1600px-аас нарийн дэлгэц дээр багасна */
const BUTTON_HEIGHT = 38;
const BUTTON_HEIGHT_COMPACT = 32;

/** Эвхэгдсэн товчны доор ч цэс давчуу харагдахгүй байх өргөн */
const MENU_WIDTH = 208;
const MENU_EDGE_PADDING = 6;
const MENU_ITEM_PADDING_BLOCK = 8;
const MENU_ITEM_PADDING_INLINE = 12;

/** Дэд цэс нь `MENU_WIDTH`-тэй ижил өргөнтэй байхын тулд элементэд өгөх өргөн */
const MENU_ITEM_MIN_WIDTH = MENU_WIDTH - (MENU_EDGE_PADDING + MENU_ITEM_PADDING_INLINE) * 2;

/**
 * Зай нэг хэмжүүртэй: товч → цэс, эцэг цэс → дэд цэс хоорондын зай нь цэсний
 * ирмэгийн зайтай (`MENU_EDGE_PADDING`) ижил, элемент хоорондын зай тогтмол.
 */
const MENU_GAP = MENU_EDGE_PADDING;
const MENU_ITEM_GAP = 2;

/**
 * Дэд цэсний байрлал. antd дэд цэсийг гарчгийн баруун ирмэгт (эцэг цэсний
 * padding дотор) наалдуулж тавьдаг тул:
 * - x: эцэг цэсний padding-ийг давж, дээр нь `MENU_GAP` зай авна;
 * - y: дэд цэсний дээд padding-ийн хэмжээгээр дээш татаж эхний мөрийг гарчигтай
 *   нэг шугамд тавина.
 */
const SUBMENU_POPUP_OFFSET = [MENU_EDGE_PADDING + MENU_GAP, -MENU_EDGE_PADDING];

/** antd элементүүдэд `margin: 0` өгдөг тул сүүлчийнхээс бусдад доод зай нэмнэ */
const withItemGap = (items) =>
  items.map((item, index) =>
    index < items.length - 1 ? { ...item, style: { marginBottom: MENU_ITEM_GAP } } : item
  );

const MENU_TEXT_COLOR = '#4e5562';
const MENU_ICON_COLOR = '#8993a5';

/**
 * Дүрс, нэрийг нэг мөрөнд байрлуулна.
 *
 * `Space` нь `inline-flex` + босоо центр — өөрөөр хэлбэл шошго нь МӨРӨН ДОТОР
 * үлдэнэ. Энэ нь дэд цэсний сумны хувьд чухал: antd сумыг `position: absolute`
 * -аар, зөвхөн `inset-inline-end` өгч тавьдаг тул босоо байрлалаа гарчгийн
 * мөрнөөс авна. Шошгыг блок (`display: flex`) болговол сум мөрнөөс доош
 * зөрнө. Хүрээ нь inline хэвээр байх учир дүрс ч блок болж нэр доош бөхөхгүй.
 */
const MenuLabel = ({ icon, minWidth, children }) => (
  <Space size={8} style={{ minWidth, fontWeight: 500 }}>
    {icon ? <span style={{ display: 'inline-flex', color: MENU_ICON_COLOR }}>{icon}</span> : null}
    {children}
  </Space>
);

/**
 * Дэд цэсний сум.
 *
 * antd үүн дээр `-submenu-expand-icon` класс нэмнэ — тэр нь `position: absolute`
 * + `inset-inline-end` л өгдөг, босоо байрлалыг заадаггүй тул сум гарчгийн
 * мөрний баримжаагаар (baseline) тавигдаж центрээс зөрдөг. `top: 50%` +
 * `translateY(-50%)`-аар гарчгийн өндрийн яг центрт тавина.
 *
 * Өөрийн сум өгснөөр antd-ийн `-submenu-arrow-icon` классын хэлбэр
 * (өнгө/хэмжээ) хамаарахгүй болох тул тэдгээрийг инлайнаар өглөө.
 */
const MENU_ARROW_SIZE = 12;

const menuArrow = (
  <span style={{ display: 'inline-flex', top: '50%', transform: 'translateY(-50%)' }}>
    <RightOutlined style={{ fontSize: MENU_ARROW_SIZE, color: MENU_ICON_COLOR }} />
  </span>
);

const NavButton = ({ collapsed = false }) => {
  const history = useHistory();
  const screens = Grid.useBreakpoint();

  const menuItems = useStore((state) => state.menuItems);

  const [createUserVisible, setCreateUserVisible] = useState(false);
  const [createBrandVisible, setCreateBrandVisible] = useState(false);

  /** `xxl` = 1600px-аас өргөн. Хэмжигдэх хүртэл өргөн дэлгэц гэж үзнэ. */
  const compact = screens.xxl === false;

  const dropdownItems = useMemo(() => {
    /**
     * Backend-ээс ирэх меню зөвхөн нэр/замыг л агуулдаг тул модал онгойлгох
     * шаардлагатай элементүүдийг `<хэсгийн нэр>|<элементийн нэр>` гэсэн
     * түлхүүрээр тааруулна. Бусад элемент нь өөрийн `path`-аар шилжинэ.
     */
    const itemActions = {
      'Бусад|Харилцагч': () => history.push('/clients/add'),
      'Бусад|Брэнд': () => setCreateBrandVisible(true),
      'Профайл|Салбар': () => history.push('/settings/branch/add'),
      'Профайл|Ажилтан': () => setCreateUserVisible(true),
    };

    // SideMenu-тэй ижил дүрмээр lucide icon-г нэрээр сонгоно.
    const renderIcon = (name) => {
      const SectionIcon = Icon?.[name] ?? Icon.Layers;
      return <SectionIcon size={16} strokeWidth={1.8} />;
    };

    const sections = (menuItems ?? [])
      .filter((section) => Array.isArray(section?.items) && section.items.length > 0)
      .map((section, sectionIndex) => ({
        key: section?.code ?? section?.title ?? `section-${sectionIndex}`,
        label: <MenuLabel icon={renderIcon(section?.icon)}>{section?.title}</MenuLabel>,
        popupOffset: SUBMENU_POPUP_OFFSET,
        children: withItemGap(
          section.items.map((item, itemIndex) => {
            const action = itemActions[`${section?.title}|${item?.name}`];
            const onClick = action ?? (item?.path ? () => history.push(item.path) : null);

            return {
              key: item?.code ?? item?.name ?? `${sectionIndex}-${itemIndex}`,
              // Дэд цэс нь эцэг цэстэйгээ ижил өргөнтэй байхаар шошго нь өргөнөө барина
              label: <MenuLabel minWidth={MENU_ITEM_MIN_WIDTH}>{item?.name}</MenuLabel>,
              disabled: !onClick,
              onClick: onClick ?? undefined,
            };
          })
        ),
      }));

    return withItemGap(sections);
  }, [menuItems, history]);

  const hasMenu = dropdownItems.length > 0;

  /**
   * Эвхэгдсэн үед `children`-гүй үлдээнэ — antd өөрөө дүрс-товч (`icon-only`)
   * болгож дөрвөлжин хэлбэрт оруулна, өргөнийг нь `controlHeight` тодорхойлно.
   */
  const button = (
    <Button icon={<PlusOutlined />} block={!collapsed} disabled={!hasMenu}>
      {collapsed ? null : 'Шинэ'}
    </Button>
  );

  const trigger = hasMenu ? (
    <Dropdown
      menu={{ items: dropdownItems, style: { minWidth: MENU_WIDTH }, expandIcon: menuArrow }}
      trigger={['click']}
      placement={collapsed ? 'bottomLeft' : 'bottomRight'}
      // antd-ийн анхны 4px-ийн оронд бусад зайтай ижил
      align={{ offset: [0, MENU_GAP] }}
    >
      {button}
    </Dropdown>
  ) : (
    button
  );

  return (
    <>
      <ConfigProvider
        theme={{
          components: {
            Button: {
              controlHeight: compact ? BUTTON_HEIGHT_COMPACT : BUTTON_HEIGHT,
              fontWeight: 600,
              // Outline (default) хувилбар: анхнаасаа брэндийн хөх, hover-т тодрох
              defaultColor: BRAND_COLOR,
              defaultBg: brandAlpha(0.06),
              defaultBorderColor: BRAND_COLOR,
              defaultHoverColor: BRAND_COLOR_HOVER,
              defaultHoverBg: brandAlpha(0.12),
              defaultHoverBorderColor: BRAND_COLOR_HOVER,
              defaultActiveColor: BRAND_COLOR_HOVER,
              defaultActiveBg: brandAlpha(0.16),
              defaultActiveBorderColor: BRAND_COLOR_HOVER,
              // Дүүрэн (primary) хувилбар руу солиход зөөлөн сүүдэр нэмэгдэнэ
              primaryShadow: `0 4px 12px ${brandAlpha(0.24)}`,
            },
            Dropdown: {
              // `paddingXXS` нь цэсний гадна ирмэгийн зайг (эцэг ба дэд цэс) тодорхойлно
              paddingXXS: MENU_EDGE_PADDING,
              paddingBlock: MENU_ITEM_PADDING_BLOCK,
              controlPaddingHorizontal: MENU_ITEM_PADDING_INLINE,
              colorText: MENU_TEXT_COLOR,
              fontSize: compact ? 12 : 14,
              boxShadowSecondary: '0 12px 32px rgba(160, 163, 189, 0.28)',
            },
          },
        }}
      >
        <Flex justify="center" style={{ padding: collapsed ? '16px 8px 12px' : '16px 16px 12px' }}>
          {collapsed ? (
            <Tooltip title="Шинэ" placement="right">
              <span style={{ display: 'inline-flex' }}>{trigger}</span>
            </Tooltip>
          ) : (
            trigger
          )}
        </Flex>
      </ConfigProvider>

      {createUserVisible && (
        <CreateUser visible={createUserVisible} onCancel={() => setCreateUserVisible(false)} />
      )}
      {createBrandVisible && (
        <CreateBrand visible={createBrandVisible} onCancel={() => setCreateBrandVisible(false)} />
      )}
    </>
  );
};

export default NavButton;
